import { find, map } from 'lodash-es'
import { actionTree, getterTree, mutationTree } from 'typed-vuex'
import { addDays, dateDiff } from '~/lib/utils/date'
import { setState } from '~/lib/utils/store'

const FREE_APPLICATION_LIMIT = 1
const TIMEOUT_INCREASE_FACTOR = 1.7
const WEEKLY_APPLICATION_LIMIT = 3

export const state = () => ({
  fetchAttempts: 0,
  fetchAttemptsLimit: 9,
  fetchTimeout: 1000,
  isFetching: false,
  list: [],
  recommendedAppliedCount: 0,
  recommendedFetchingReached: false,
  recommendedGeneratedAt: 0
})

export const mutations = mutationTree(
  state,
  {
    setState
  }
)

export const getters = getterTree(
  state,
  {
    daysToNextGeneration: (_state, { nextGenerationDate }) => {
      return dateDiff(new Date(), nextGenerationDate)
    },
    ids: ({ list }) => map(list, 'scholarshipId'),
    isApplicationJustSubmitted: (_state, _getters, rootState) => {
      return rootState.route.query?.status === 'success'
    },
    isExpectingRecommendationsToExist: (_state, _getters, rootState, rootGetters) => {
      const { createdAt } = rootState.auth.user || {}
      if (!createdAt) { return false }

      const userCreationDate = new Date(createdAt)
      const oneWeekAgo = addDays(new Date(), -7)
      const isFreemium = rootGetters['account/isFreemium']
      const isPro = rootGetters['account/isPro']
      const isMaintenanceMode = rootGetters['account/isMaintenanceMode']

      return isMaintenanceMode || (
        isFreemium
          ? userCreationDate > oneWeekAgo
          : isPro
      )
    },
    isFreeApplicationLimitReached: (state) => {
      return state.recommendedAppliedCount >= FREE_APPLICATION_LIMIT
    },
    isRecommended: (_state, { ids }) =>
      ({ scholarshipId }) =>
        ids.includes(scholarshipId),
    isWeeklyApplicationLimitReached: (state) => {
      return state.recommendedAppliedCount >= WEEKLY_APPLICATION_LIMIT
    },
    nextGenerationDate: ({ recommendedGeneratedAt }) => {
      const lastGenerationDate = new Date(recommendedGeneratedAt * 1000)
      const nextGenerationDate = addDays(lastGenerationDate, 7)
      return nextGenerationDate
    }
  }
)

export const actions = actionTree(
  { state, mutations },
  {
    fetchRecommendedScholarships({ state, commit, rootState }, { isForced }: { isForced?: boolean } = {}): Promise<unknown> {
      const {
        isExpectingRecommendationsToExist,
        recommendedGeneratedAt
      } = this.app.$accessor.recommendation
      const shouldRequest = isForced ||
        (!recommendedGeneratedAt && isExpectingRecommendationsToExist)

      if (!shouldRequest) {
        return Promise.resolve()
      }

      commit('setState', { stateName: 'isFetching', data: true })

      return new Promise((resolve) => {
        const fetchUntilSuccess = (res: Axios.AxiosResponse) => {
          const ignoredScholarshipId = rootState.tabList.recentlyIgnoredScholarshipId
          const hasIgnoredIdInResponse = !!find(res.data?.data, { scholarshipId: ignoredScholarshipId })

          if (!res.status || res.status === 401) { return }

          if (res.status !== 200 || (res.data.meta && !res.data.meta.generatedAt) || hasIgnoredIdInResponse) {
            if (state.fetchAttempts >= state.fetchAttemptsLimit) {
              commit('setState', { stateName: 'isFetching', data: true })
              commit('setState', { stateName: 'recommendedFetchingReached', data: true })
              throw new Error('Attempts to extract recommended scholarships have peaked.')
            }

            setTimeout(() => {
              this.app.$accessor.recommendation.fetchRecommendedScholarships({ isForced })
            }, state.fetchTimeout)

            commit('setState', { stateName: 'fetchAttempts', data: state.fetchAttempts + 1 })
            // last timeout step will be 118.587876497 seconds (~2 minutes)
            // total run time will be 166.98268070999995 (~3 minutes)
            commit('setState', { stateName: 'fetchTimeout', data: state.fetchTimeout * TIMEOUT_INCREASE_FACTOR })
            return
          }

          if (res?.data?.meta?.generatedAt && !hasIgnoredIdInResponse) {
            commit('setState', { stateName: 'recommendedAppliedCount', data: res.data.meta.appliedCount })
            commit('setState', { stateName: 'recommendedGeneratedAt', data: res.data.meta.generatedAt })
            commit('setState', { stateName: 'list', data: res.data.data })
            resolve(res.data.data)
            commit('setState', { stateName: 'isFetching', data: false })
          }
        }

        this.$api.recommended.fetchScholarships()
          .then(fetchUntilSuccess)
          .catch(fetchUntilSuccess)
      })
    }

  }
)
